import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../DataContext";
import axios from "axios";

// ── Static platform data ──────────────────────────────────────────────────────

const HIERARCHY_TIERS = [
  { tier: "T1", label: "Initiative",  icon: "◈", color: "#C9A84C", desc: "Strategic theme / goal spanning multiple portfolios" },
  { tier: "T2", label: "Portfolio",   icon: "⬡", color: "#00D4FF", desc: "Investment envelope aligned to an initiative" },
  { tier: "T3", label: "Programme",   icon: "◉", color: "#A78BFA", desc: "Group of related projects managed as a unit" },
  { tier: "T4", label: "Project",     icon: "▦", color: "#00E676", desc: "Specific, time-bound deliverable" },
  { tier: "T5", label: "Epic",        icon: "◧", color: "#FF9800", desc: "Large body of work within a project" },
  { tier: "T6", label: "Work Item",   icon: "⬟", color: "#64B5F6", desc: "Story / Task / Bug — individual piece of work" },
  { tier: "T7", label: "Sub-task",    icon: "◫", color: "#8BB8D8", desc: "Smallest unit — breaks a work item into steps" },
];

const ANALYTICAL_MODULES = [
  { page: "P-00", path: "/platform-map",     icon: "◎", label: "Platform Map",             desc: "Full capabilities reference, reports index, hierarchy model, credentials", color: "#C9A84C", roles: "All" },
  { page: "P-01", path: "/portfolio",        icon: "◈", label: "Portfolio Overview",        desc: "Strategic health, RAG distribution, portfolio KPIs, CPI/SPI trends", color: "#00D4FF", roles: "All" },
  { page: "P-02", path: "/health",           icon: "⬡", label: "Project Health",            desc: "ML failure prediction, EVM per project, milestone status, delivery forecast", color: "#00E676", roles: "Analyst+" },
  { page: "P-03", path: "/benefits",         icon: "◎", label: "Benefits Realisation",      desc: "ROI tracking, value delivery, realisation curves, benefits pipeline", color: "#A78BFA", roles: "Analyst+" },
  { page: "P-04", path: "/risk",             icon: "⚠", label: "Risk Intelligence",         desc: "Risk register, probability/impact scoring, velocity trends, mitigation", color: "#FF3D57", roles: "Analyst+" },
  { page: "P-05", path: "/resources",        icon: "◉", label: "Resource Intelligence",     desc: "Utilisation rates, efficiency scores, bottleneck identification, cost rates", color: "#FFB800", roles: "Analyst+" },
  { page: "P-06", path: "/financial",        icon: "▦", label: "Financial Performance",     desc: "EVM analysis, budget burn rate, cost variance, EAC/ETC forecasting", color: "#00D4FF", roles: "Analyst+" },
  { page: "P-07", path: "/schedule",         icon: "◧", label: "Schedule & Critical Path",  desc: "Milestone intelligence, SPI trending, Gantt-style schedule view", color: "#64B5F6", roles: "Analyst+" },
  { page: "P-08", path: "/dependencies",     icon: "⬟", label: "Interdependency Risk",      desc: "Cross-project dependency mapping, cascade risk identification", color: "#FF9800", roles: "Analyst+" },
  { page: "P-09", path: "/workforce",        icon: "◫", label: "PM Workforce Quality",      desc: "PM performance scoring, competency gap analysis, coaching recommendations", color: "#00E676", roles: "Consultant+" },
  { page: "P-10", path: "/rationalization",  icon: "◬", label: "Portfolio Rationalisation", desc: "Kill / Continue / Invest decisions, ROI ranking, strategic alignment", color: "#A78BFA", roles: "Consultant+" },
  { page: "P-11", path: "/executive",        icon: "★", label: "Executive Board View",      desc: "Eagle-eye C-suite dashboard, top decisions, strategic summary", color: "#C9A84C", roles: "Board+" },
  { page: "P-12", path: "/ai-agent",         icon: "◑", label: "AI Agent Console",          desc: "Claude-powered PMO advisor — natural language portfolio queries", color: "#00D4FF", roles: "Analyst+" },
  { page: "P-13", path: "/registry",         icon: "◈", label: "Portfolio Registry",        desc: "Full P3I hierarchy browser, CRUD, status snapshots, SQLite persistence", color: "#C9A84C", roles: "Analyst+" },
  { page: "P-14", path: "/reports",          icon: "◇", label: "Reports Centre",            desc: "Generate, preview, and download all portfolio reports in Excel/CSV/JSON", color: "#00E676", roles: "Analyst+" },
];

const REPORT_TYPES = [
  {
    id: "portfolio_summary",
    label: "Portfolio Summary",
    level: "Portfolio",
    icon: "◈",
    color: "#C9A84C",
    formats: ["Excel", "CSV", "JSON"],
    contents: "All 12 projects · CPI/SPI trends · RAG distribution · Budget burn · Top KPIs",
  },
  {
    id: "project_health",
    label: "Project Health Report",
    level: "Project",
    icon: "⬡",
    color: "#00E676",
    formats: ["Excel", "CSV", "JSON"],
    contents: "Per-project EVM · Milestone status · Failure probability · Forecast-to-complete",
  },
  {
    id: "risk_register",
    label: "Risk Register",
    level: "Portfolio",
    icon: "⚠",
    color: "#FF3D57",
    formats: ["Excel", "CSV", "JSON"],
    contents: "All risks · Probability/impact matrix · Budget exposure · Mitigation status",
  },
  {
    id: "evm_report",
    label: "EVM Performance Report",
    level: "Programme",
    icon: "▦",
    color: "#00D4FF",
    formats: ["Excel", "CSV", "JSON"],
    contents: "CV · SV · CPI · SPI · EAC · ETC · VAC · TCPI per project",
  },
  {
    id: "board_pack",
    label: "Board Pack",
    level: "Board",
    icon: "★",
    color: "#A78BFA",
    formats: ["Excel", "JSON"],
    contents: "Executive summary · Top decisions required · Key portfolio metrics · RAG summary",
  },
];

const EVM_METRICS = [
  { abbr: "BAC",  name: "Budget at Completion",          formula: "Baseline",            meaning: "Total approved budget" },
  { abbr: "PV",   name: "Planned Value",                  formula: "BAC × % planned",     meaning: "Work scheduled to be done" },
  { abbr: "EV",   name: "Earned Value",                   formula: "BAC × % complete",    meaning: "Work actually accomplished" },
  { abbr: "AC",   name: "Actual Cost",                    formula: "Actual spend",        meaning: "Cost incurred for work done" },
  { abbr: "CV",   name: "Cost Variance",                  formula: "EV − AC",             meaning: "+ = under budget" },
  { abbr: "SV",   name: "Schedule Variance",              formula: "EV − PV",             meaning: "+ = ahead of schedule" },
  { abbr: "CPI",  name: "Cost Performance Index",         formula: "EV / AC",             meaning: "> 1 = cost-efficient" },
  { abbr: "SPI",  name: "Schedule Performance Index",     formula: "EV / PV",             meaning: "> 1 = ahead of schedule" },
  { abbr: "EAC",  name: "Estimate at Completion",         formula: "BAC / CPI",           meaning: "Forecast final cost" },
  { abbr: "ETC",  name: "Estimate to Complete",           formula: "EAC − AC",            meaning: "Remaining cost forecast" },
  { abbr: "VAC",  name: "Variance at Completion",         formula: "BAC − EAC",           meaning: "Forecast over/under at end" },
  { abbr: "TCPI", name: "To-Complete Performance Index",  formula: "(BAC−EV)/(BAC−AC)",   meaning: "Required future efficiency" },
  { abbr: "CSI",  name: "Cost-Schedule Index",            formula: "CPI × SPI",           meaning: "Composite health indicator" },
];

const CREDENTIALS = [
  { username: "hassaan",   password: "ShurikenPMO#Hassaan2026!", role: "PMO Director",      color: "#00D4FF",  access: "Full platform access" },
  { username: "board",     password: "Board@PMO2026!",           role: "Board Member",       color: "#C9A84C",  access: "P-01, P-11, Reports" },
  { username: "ceo",       password: "CLevel@PMO2026!",          role: "C-Level Executive",  color: "#A78BFA",  access: "Full except admin" },
  { username: "admin",     password: "Admin@PMO2026!",           role: "System Admin",       color: "#FF6B35",  access: "Full + user management" },
  { username: "consultant",password: "Consult@PMO2026!",         role: "Consultant",         color: "#00E676",  access: "Analytics + reports, no admin" },
  { username: "analyst",   password: "Analyst@PMO2026!",         role: "Analyst",            color: "#64B5F6",  access: "Dashboards + reports" },
  { username: "pmostaff",  password: "Staff@PMO2026!",           role: "PMO Staff",          color: "#8BB8D8",  access: "Portfolio view only" },
];

const LEVEL_COLORS = { Board: "#C9A84C", Portfolio: "#00D4FF", Programme: "#A78BFA", Project: "#00E676" };

// ── Helper components ─────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle, color, theme }) {
  return (
    <div style={{ marginBottom: "16px", paddingBottom: "10px", borderBottom: `1px solid ${theme.cardBorder}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "16px", color, filter: `drop-shadow(0 0 5px ${color}88)` }}>{icon}</span>
        <span style={{ fontFamily: "'Orbitron', monospace", fontSize: "12px", fontWeight: 700, color, letterSpacing: "1.5px" }}>
          {title.toUpperCase()}
        </span>
      </div>
      {subtitle && <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "4px", marginLeft: "24px" }}>{subtitle}</div>}
    </div>
  );
}

function Card({ children, theme, style = {} }) {
  return (
    <div style={{
      background: theme.card, border: `1px solid ${theme.cardBorder}`,
      borderRadius: "10px", padding: "18px",
      ...style,
    }}>{children}</div>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{
      fontSize: "9px", padding: "2px 7px", borderRadius: "10px",
      background: `${color}22`, color, border: `1px solid ${color}44`,
      fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
    }}>{label}</span>
  );
}

// ── Tab button ────────────────────────────────────────────────────────────────

function TabBar({ tabs, active, onChange, theme }) {
  return (
    <div style={{
      display: "flex", gap: 0,
      borderBottom: `1px solid ${theme.cardBorder}`,
      marginBottom: "24px",
    }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: "10px 18px", cursor: "pointer",
          background: "transparent", border: "none",
          borderBottom: active === t.id ? `2px solid ${theme.accent}` : "2px solid transparent",
          color: active === t.id ? theme.accent : theme.textMuted,
          fontSize: "11px", fontWeight: active === t.id ? 700 : 400,
          letterSpacing: "0.3px", transition: "all 0.15s",
          whiteSpace: "nowrap",
        }}>{t.icon} {t.label}</button>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PlatformMap() {
  const { theme }  = useTheme();
  const navigate   = useNavigate();
  const [tab, setTab]         = useState("modules");
  const [summary, setSummary] = useState(null);
  const [copied, setCopied]   = useState(null);

  useEffect(() => {
    axios.get("https://pmo-platform.onrender.com/api/registry/summary")
      .then(r => setSummary(r.data))
      .catch(() => {});
  }, []);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const TABS = [
    { id: "modules",    icon: "◈", label: "Analytical Modules" },
    { id: "reports",    icon: "◇", label: "Reports" },
    { id: "hierarchy",  icon: "⬡", label: "Hierarchy Model" },
    { id: "evm",        icon: "▦", label: "EVM Metrics" },
    { id: "registry",   icon: "◉", label: "Registry" },
    { id: "access",     icon: "★", label: "Access & Credentials" },
  ];

  return (
    <div style={{
      minHeight: "100vh", background: theme.bg,
      color: theme.textPrimary, fontFamily: "'Exo 2', sans-serif",
    }}>
      {/* Page header */}
      <div style={{
        padding: "16px 28px", borderBottom: `1px solid ${theme.cardBorder}`,
        background: theme.headerGrad,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "20px", color: theme.accent, filter: `drop-shadow(0 0 6px ${theme.accent}88)` }}>◎</span>
            <span style={{ fontFamily: "'Orbitron', monospace", fontSize: "13px", fontWeight: 700, color: theme.accent, letterSpacing: "2px" }}>
              PLATFORM MAP
            </span>
            <span style={{
              fontSize: "9px", padding: "2px 7px",
              background: `${theme.accent}15`, color: theme.accent,
              border: `1px solid ${theme.accent}33`, borderRadius: "10px",
              fontFamily: "'Share Tech Mono', monospace",
            }}>P-00</span>
          </div>
          <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "3px" }}>
            Complete reference for all platform capabilities, reports, and data structures
          </div>
        </div>

        {/* Live registry counts */}
        {summary && (
          <div style={{ display: "flex", gap: "6px" }}>
            {[
              { label: "Initiatives", value: summary.initiatives, color: "#C9A84C" },
              { label: "Portfolios",  value: summary.portfolios,  color: "#00D4FF" },
              { label: "Programmes",  value: summary.programs,    color: "#A78BFA" },
              { label: "Projects",    value: summary.projects,    color: "#00E676" },
              { label: "Epics",       value: summary.epics,       color: "#FF9800" },
              { label: "Work Items",  value: summary.work_items,  color: "#64B5F6" },
            ].map(c => (
              <div key={c.label} style={{
                padding: "4px 9px", borderRadius: "8px", textAlign: "center",
                background: `${c.color}10`, border: `1px solid ${c.color}22`,
              }}>
                <div style={{ fontSize: "13px", fontWeight: 700, color: c.color, fontFamily: "'Share Tech Mono', monospace" }}>{c.value}</div>
                <div style={{ fontSize: "8px", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.3px" }}>{c.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: "24px 28px", maxWidth: "1100px" }}>
        <TabBar tabs={TABS} active={tab} onChange={setTab} theme={theme} />

        {/* ── Tab: Analytical Modules ────────────────────────────── */}
        {tab === "modules" && (
          <div>
            <SectionHeader icon="◈" title="Analytical Modules" color={theme.accent}
              subtitle="15 pages covering the full PMO intelligence stack. Click any row to navigate." theme={theme} />

            <div style={{
              background: theme.card, border: `1px solid ${theme.cardBorder}`,
              borderRadius: "10px", overflow: "hidden",
            }}>
              {/* Table header */}
              <div style={{
                display: "grid", gridTemplateColumns: "60px 200px 1fr 100px",
                padding: "8px 14px", background: `${theme.cardBorder}44`,
                fontSize: "9px", color: theme.textMuted,
                textTransform: "uppercase", letterSpacing: "1px",
                fontWeight: 700,
              }}>
                <span>Page</span>
                <span>Module</span>
                <span>Capabilities</span>
                <span>Min Role</span>
              </div>

              {ANALYTICAL_MODULES.map((m, i) => (
                <div
                  key={m.page}
                  onClick={() => navigate(m.path)}
                  style={{
                    display: "grid", gridTemplateColumns: "60px 200px 1fr 100px",
                    padding: "10px 14px", cursor: "pointer",
                    borderTop: i > 0 ? `1px solid ${theme.cardBorder}44` : "none",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = `${m.color}08`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ fontSize: "12px", color: m.color }}>{m.icon}</span>
                    <span style={{
                      fontSize: "9px", padding: "1px 5px",
                      background: `${m.color}15`, color: m.color,
                      borderRadius: "4px",
                      fontFamily: "'Share Tech Mono', monospace",
                    }}>{m.page}</span>
                  </div>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: theme.textPrimary, alignSelf: "center" }}>
                    {m.label}
                  </div>
                  <div style={{ fontSize: "11px", color: theme.textMuted, alignSelf: "center", lineHeight: 1.4 }}>
                    {m.desc}
                  </div>
                  <div style={{ alignSelf: "center" }}>
                    <Badge label={m.roles} color={m.color} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "10px", fontSize: "10px", color: theme.textMuted }}>
              ↗ Click any row to navigate directly to that module
            </div>
          </div>
        )}

        {/* ── Tab: Reports ──────────────────────────────────────── */}
        {tab === "reports" && (
          <div>
            <SectionHeader icon="◇" title="Reports" color={theme.accent}
              subtitle="All reports available from P-14 Reports Centre (/reports). Generated as Excel, CSV, or JSON." theme={theme} />

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              {REPORT_TYPES.map(rt => (
                <Card key={rt.id} theme={theme} style={{ borderLeft: `3px solid ${rt.color}` }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
                    <span style={{ fontSize: "20px", color: rt.color, filter: `drop-shadow(0 0 5px ${rt.color}88)`, flexShrink: 0 }}>{rt.icon}</span>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: theme.textPrimary }}>{rt.label}</span>
                        <Badge label={rt.level} color={LEVEL_COLORS[rt.level] || "#8BB8D8"} />
                      </div>
                      <div style={{ fontSize: "11px", color: theme.textMuted }}>{rt.contents}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <span style={{ fontSize: "10px", color: theme.textMuted }}>Formats:</span>
                    {rt.formats.map(f => (
                      <span key={f} style={{
                        fontSize: "10px", padding: "2px 8px", borderRadius: "6px",
                        background: `${rt.color}12`, color: rt.color,
                        border: `1px solid ${rt.color}33`, fontWeight: 600,
                      }}>{f}</span>
                    ))}
                    <button
                      onClick={() => navigate("/reports")}
                      style={{
                        marginLeft: "auto", padding: "4px 12px", borderRadius: "6px",
                        cursor: "pointer", background: `${rt.color}12`,
                        border: `1px solid ${rt.color}33`, color: rt.color,
                        fontSize: "10px", fontWeight: 600,
                      }}
                    >Generate →</button>
                  </div>
                </Card>
              ))}
            </div>

            {/* EVM quick ref here too */}
            <Card theme={theme}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: theme.textPrimary, marginBottom: "12px" }}>
                Report Coverage by Hierarchy Level
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
                {[
                  { level: "Board",     color: "#C9A84C", reports: ["Board Pack"] },
                  { level: "Portfolio", color: "#00D4FF", reports: ["Portfolio Summary", "Risk Register"] },
                  { level: "Programme", color: "#A78BFA", reports: ["EVM Performance Report"] },
                  { level: "Project",   color: "#00E676", reports: ["Project Health Report"] },
                ].map(g => (
                  <div key={g.level}>
                    <div style={{ fontSize: "10px", color: g.color, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px",
                      paddingBottom: "4px", borderBottom: `1px solid ${g.color}33`,
                    }}>{g.level}</div>
                    {g.reports.map(r => (
                      <div key={r} style={{ fontSize: "11px", color: theme.textSecondary, marginBottom: "4px", paddingLeft: "6px" }}>
                        · {r}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Tab: Hierarchy Model ──────────────────────────────── */}
        {tab === "hierarchy" && (
          <div>
            <SectionHeader icon="⬡" title="Hierarchy Model" color={theme.accent}
              subtitle="7-tier PMO management structure from strategic initiative to individual sub-task." theme={theme} />

            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "24px" }}>
              {HIERARCHY_TIERS.map((t, i) => (
                <div key={t.tier} style={{
                  display: "flex", alignItems: "center", gap: "14px",
                  padding: "12px 16px",
                  marginLeft: `${i * 18}px`,
                  background: theme.card, border: `1px solid ${theme.cardBorder}`,
                  borderLeft: `3px solid ${t.color}`,
                  borderRadius: "8px",
                }}>
                  <span style={{
                    fontSize: "10px", padding: "2px 6px",
                    background: `${t.color}18`, color: t.color,
                    borderRadius: "4px", flexShrink: 0,
                    fontFamily: "'Share Tech Mono', monospace", fontWeight: 700,
                  }}>{t.tier}</span>
                  <span style={{ fontSize: "16px", color: t.color, width: "20px",
                    filter: `drop-shadow(0 0 4px ${t.color}88)` }}>{t.icon}</span>
                  <span style={{ fontSize: "13px", fontWeight: 700, color: theme.textPrimary, width: "100px" }}>
                    {t.label}
                  </span>
                  <span style={{ fontSize: "11px", color: theme.textMuted }}>{t.desc}</span>
                  {i === 0 && <Badge label="Highest" color={t.color} />}
                  {i === 6 && <Badge label="Lowest" color={t.color} />}
                </div>
              ))}
            </div>

            <Card theme={theme}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: theme.textPrimary, marginBottom: "10px" }}>
                Jira Mapping
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "11px" }}>
                {[
                  { pmop: "Initiative", jira: "Initiative (top-level cross-project theme)", color: "#C9A84C" },
                  { pmop: "Portfolio",  jira: "Portfolio / Programme (Jira Plans)", color: "#00D4FF" },
                  { pmop: "Programme",  jira: "Programme / Project in Jira Advanced Roadmaps", color: "#A78BFA" },
                  { pmop: "Project",    jira: "Project (Jira project board)", color: "#00E676" },
                  { pmop: "Epic",       jira: "Epic — highest-level Jira work item", color: "#FF9800" },
                  { pmop: "Work Item",  jira: "Story / Task / Bug", color: "#64B5F6" },
                  { pmop: "Sub-task",   jira: "Sub-task", color: "#8BB8D8" },
                ].map(m => (
                  <div key={m.pmop} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <span style={{
                      fontSize: "11px", fontWeight: 700, color: m.color, width: "90px",
                      flexShrink: 0,
                    }}>{m.pmop}</span>
                    <span style={{ color: theme.textMuted }}>→</span>
                    <span style={{ color: theme.textSecondary }}>{m.jira}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Tab: EVM Metrics ──────────────────────────────────── */}
        {tab === "evm" && (
          <div>
            <SectionHeader icon="▦" title="EVM Metrics Reference" color={theme.accent}
              subtitle="13 Earned Value Management metrics calculated per project. Used across P-02, P-06, and all EVM reports." theme={theme} />

            <Card theme={theme} style={{ marginBottom: "16px" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "60px 200px 180px 1fr",
                gap: "1px",
                background: theme.cardBorder,
                borderRadius: "6px", overflow: "hidden",
              }}>
                {/* Header */}
                {["Abbr", "Metric Name", "Formula", "Interpretation"].map(h => (
                  <div key={h} style={{
                    padding: "8px 12px", background: `${theme.cardBorder}88`,
                    fontSize: "9px", color: theme.textMuted,
                    textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700,
                  }}>{h}</div>
                ))}
                {/* Rows */}
                {EVM_METRICS.map((m, i) => {
                  const isHealth = ["CPI", "SPI", "CSI"].includes(m.abbr);
                  const color = isHealth ? theme.accent : theme.textPrimary;
                  return (
                    <React.Fragment key={m.abbr}>
                      <div style={{ padding: "9px 12px", background: theme.card }}>
                        <span style={{
                          fontFamily: "'Share Tech Mono', monospace",
                          fontWeight: 700, fontSize: "12px",
                          color: isHealth ? theme.accent : theme.cyan,
                        }}>{m.abbr}</span>
                      </div>
                      <div style={{ padding: "9px 12px", background: theme.card, fontSize: "11px", color: theme.textPrimary, alignSelf: "center" }}>
                        {m.name}
                      </div>
                      <div style={{
                        padding: "9px 12px", background: theme.card,
                        fontFamily: "'Share Tech Mono', monospace", fontSize: "10px",
                        color: theme.textMuted, alignSelf: "center",
                      }}>{m.formula}</div>
                      <div style={{ padding: "9px 12px", background: theme.card, fontSize: "11px", color: theme.textSecondary, alignSelf: "center" }}>
                        {m.meaning}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </Card>

            <Card theme={theme}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: theme.textPrimary, marginBottom: "10px" }}>RAG Thresholds Applied</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                {[
                  { status: "GREEN",  condition: "CPI ≥ 1.00 AND SPI ≥ 0.95",     color: "#00E676", label: "On Track" },
                  { status: "AMBER",  condition: "CPI ≥ 0.85 OR SPI ≥ 0.80",      color: "#FFB800", label: "At Risk" },
                  { status: "RED",    condition: "CPI < 0.85 OR SPI < 0.80",       color: "#FF3D57", label: "Critical" },
                ].map(r => (
                  <div key={r.status} style={{
                    padding: "12px", borderRadius: "8px",
                    background: `${r.color}10`, border: `1px solid ${r.color}33`,
                  }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: r.color, marginBottom: "4px" }}>
                      ● {r.status} — {r.label}
                    </div>
                    <div style={{ fontSize: "11px", color: theme.textMuted, fontFamily: "'Share Tech Mono', monospace" }}>
                      {r.condition}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ── Tab: Registry ─────────────────────────────────────── */}
        {tab === "registry" && (
          <div>
            <SectionHeader icon="◉" title="Portfolio Registry" color={theme.accent}
              subtitle="SQLite database seeded with 4 Initiatives → 4 Portfolios → 6 Programmes → 12 Projects → 25 Epics." theme={theme} />

            {/* Live counts */}
            {summary && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
                {Object.entries({
                  Initiatives: { v: summary.initiatives, c: "#C9A84C" },
                  Portfolios:  { v: summary.portfolios,  c: "#00D4FF" },
                  Programmes:  { v: summary.programs,    c: "#A78BFA" },
                  Projects:    { v: summary.projects,    c: "#00E676" },
                  Epics:       { v: summary.epics,       c: "#FF9800" },
                  "Work Items":{ v: summary.work_items,  c: "#64B5F6" },
                  "Sub-tasks": { v: summary.sub_tasks,   c: "#8BB8D8" },
                  Snapshots:   { v: summary.snapshots,   c: theme.accent },
                }).map(([k, d]) => (
                  <div key={k} style={{
                    padding: "10px 14px", borderRadius: "8px", textAlign: "center",
                    background: `${d.c}10`, border: `1px solid ${d.c}22`,
                  }}>
                    <div style={{ fontSize: "20px", fontWeight: 700, color: d.c, fontFamily: "'Share Tech Mono', monospace" }}>{d.v}</div>
                    <div style={{ fontSize: "9px", color: theme.textMuted, textTransform: "uppercase" }}>{k}</div>
                  </div>
                ))}
              </div>
            )}

            <Card theme={theme} style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: theme.textPrimary, marginBottom: "12px" }}>API Endpoints</div>
              <div style={{ display: "grid", gridTemplateColumns: "60px 280px 1fr", gap: "1px",
                background: theme.cardBorder, borderRadius: "6px", overflow: "hidden" }}>
                {[
                  { method: "GET",  path: "/api/registry/hierarchy",           desc: "Full tree: Initiative → Portfolio → Programme → Project" },
                  { method: "GET",  path: "/api/registry/summary",             desc: "Entity counts at every tier" },
                  { method: "POST", path: "/api/registry/initiatives",         desc: "Create a new initiative" },
                  { method: "GET",  path: "/api/registry/initiatives/{id}",    desc: "Get one initiative" },
                  { method: "PUT",  path: "/api/registry/initiatives/{id}",    desc: "Update an initiative" },
                  { method: "POST", path: "/api/registry/portfolios",          desc: "Create a portfolio under an initiative" },
                  { method: "POST", path: "/api/registry/programs",            desc: "Create a programme under a portfolio" },
                  { method: "POST", path: "/api/registry/projects",            desc: "Create a project under a programme" },
                  { method: "POST", path: "/api/registry/epics",               desc: "Create an epic under a project" },
                  { method: "POST", path: "/api/registry/work-items",          desc: "Create a story / task / bug" },
                  { method: "POST", path: "/api/registry/sub-tasks",           desc: "Create a sub-task" },
                  { method: "POST", path: "/api/registry/snapshots",           desc: "Record a status snapshot (manual or automatic)" },
                  { method: "GET",  path: "/api/registry/snapshots/{type}/{id}", desc: "Full status history for any entity" },
                  { method: "POST", path: "/api/registry/seed",                desc: "Populate with demo data (idempotent)" },
                ].map((r, i) => {
                  const mc = r.method === "GET" ? "#00E676" : r.method === "POST" ? "#00D4FF" : r.method === "PUT" ? "#FFB800" : "#FF3D57";
                  return (
                    <React.Fragment key={i}>
                      <div style={{ padding: "7px 10px", background: theme.card }}>
                        <span style={{ fontSize: "9px", padding: "1px 5px", borderRadius: "4px",
                          background: `${mc}18`, color: mc, fontFamily: "'Share Tech Mono', monospace", fontWeight: 700 }}>
                          {r.method}
                        </span>
                      </div>
                      <div style={{ padding: "7px 10px", background: theme.card,
                        fontFamily: "'Share Tech Mono', monospace", fontSize: "10px", color: theme.textSecondary, alignSelf: "center" }}>
                        {r.path}
                      </div>
                      <div style={{ padding: "7px 10px", background: theme.card, fontSize: "11px", color: theme.textMuted, alignSelf: "center" }}>
                        {r.desc}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </Card>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => navigate("/registry")}
                style={{
                  padding: "10px 20px", borderRadius: "8px", cursor: "pointer",
                  background: `${theme.accent}15`, border: `1px solid ${theme.accent}44`,
                  color: theme.accent, fontSize: "12px", fontWeight: 600,
                }}
              >◈ Open Portfolio Registry →</button>
              <a
                href="https://pmo-platform.onrender.com/api/docs"
                target="_blank" rel="noreferrer"
                style={{
                  padding: "10px 20px", borderRadius: "8px",
                  background: `${theme.cyan}15`, border: `1px solid ${theme.cyan}44`,
                  color: theme.cyan, fontSize: "12px", fontWeight: 600, textDecoration: "none",
                  display: "inline-flex", alignItems: "center",
                }}
              >⎘ Swagger API Docs ↗</a>
            </div>
          </div>
        )}

        {/* ── Tab: Access & Credentials ─────────────────────────── */}
        {tab === "access" && (
          <div>
            <SectionHeader icon="★" title="Access & Credentials" color={theme.accent}
              subtitle="Demo login credentials. All roles are pre-configured. Session expires after 8 hours." theme={theme} />

            <Card theme={theme} style={{ marginBottom: "20px" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "130px 240px 160px 1fr",
                gap: "1px", background: theme.cardBorder,
                borderRadius: "6px", overflow: "hidden",
              }}>
                {["Username", "Password", "Role", "Access"].map(h => (
                  <div key={h} style={{
                    padding: "8px 12px", background: `${theme.cardBorder}88`,
                    fontSize: "9px", color: theme.textMuted,
                    textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700,
                  }}>{h}</div>
                ))}
                {CREDENTIALS.map((c, i) => (
                  <React.Fragment key={c.username}>
                    <div style={{ padding: "10px 12px", background: theme.card }}>
                      <span style={{
                        fontFamily: "'Share Tech Mono', monospace",
                        fontSize: "12px", fontWeight: 700, color: c.color,
                      }}>{c.username}</span>
                    </div>
                    <div
                      style={{
                        padding: "10px 12px", background: theme.card,
                        display: "flex", alignItems: "center", gap: "6px", cursor: "pointer",
                      }}
                      onClick={() => copyToClipboard(c.password, c.username)}
                      title="Click to copy"
                    >
                      <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "11px", color: theme.textSecondary }}>
                        {c.password}
                      </span>
                      <span style={{ fontSize: "10px", color: copied === c.username ? "#00E676" : theme.textMuted, flexShrink: 0 }}>
                        {copied === c.username ? "✔" : "⎘"}
                      </span>
                    </div>
                    <div style={{ padding: "10px 12px", background: theme.card, alignSelf: "center" }}>
                      <Badge label={c.role} color={c.color} />
                    </div>
                    <div style={{ padding: "10px 12px", background: theme.card, fontSize: "11px", color: theme.textMuted, alignSelf: "center" }}>
                      {c.access}
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div style={{ marginTop: "10px", fontSize: "10px", color: theme.textMuted }}>
                ⎘ Click any password row to copy to clipboard
              </div>
            </Card>

            <Card theme={theme}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: theme.textPrimary, marginBottom: "12px" }}>Role Access Matrix</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "6px 10px", textAlign: "left", color: theme.textMuted, fontWeight: 700, borderBottom: `1px solid ${theme.cardBorder}` }}>
                        Module
                      </th>
                      {["Board", "C-Level", "Director", "Admin", "Consultant", "Analyst", "Staff"].map(r => (
                        <th key={r} style={{ padding: "6px 8px", textAlign: "center", color: theme.textMuted, fontWeight: 700,
                          borderBottom: `1px solid ${theme.cardBorder}`, whiteSpace: "nowrap" }}>{r}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: "Portfolio Overview",     access: [1,1,1,1,1,1,1] },
                      { label: "Project Health",         access: [0,1,1,1,1,1,1] },
                      { label: "Benefits Realisation",   access: [0,1,1,1,1,1,0] },
                      { label: "Risk Intelligence",      access: [0,1,1,1,1,1,0] },
                      { label: "Resource Intelligence",  access: [0,1,1,1,1,1,1] },
                      { label: "Financial Performance",  access: [0,1,1,1,1,1,0] },
                      { label: "Schedule & Crit. Path",  access: [0,1,1,1,1,1,1] },
                      { label: "Interdependency Risk",   access: [0,1,1,1,1,1,0] },
                      { label: "PM Workforce Quality",   access: [0,1,1,1,1,0,0] },
                      { label: "Portfolio Rationalise",  access: [0,1,1,1,1,0,0] },
                      { label: "Executive Board View",   access: [1,1,1,1,0,0,0] },
                      { label: "AI Agent Console",       access: [0,1,1,1,1,1,0] },
                      { label: "Portfolio Registry",     access: [1,1,1,1,1,1,0] },
                      { label: "Reports Centre",         access: [1,1,1,1,1,1,0] },
                      { label: "User Management",        access: [0,0,1,1,0,0,0] },
                      { label: "Settings",               access: [0,1,1,1,0,0,0] },
                    ].map((row, i) => (
                      <tr key={row.label} style={{ background: i % 2 === 0 ? "transparent" : `${theme.card}55` }}>
                        <td style={{ padding: "7px 10px", color: theme.textSecondary, fontSize: "11px" }}>{row.label}</td>
                        {row.access.map((a, j) => (
                          <td key={j} style={{ padding: "7px 8px", textAlign: "center" }}>
                            {a ? <span style={{ color: "#00E676", fontSize: "12px" }}>✔</span>
                               : <span style={{ color: theme.cardBorder, fontSize: "12px" }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}


